import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/lib/saathiClient";

const field = "bg-[#FFFBF5] border border-[#EDE0D4] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#E07B39]";

function CreateOrgForm({ token, onCreated }) {
  const [f, setF] = useState({ name: "", slug: "", email: "", password: "" });
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await axios.post(`${API}/admin/orgs`, { ...f, slug: f.slug || null }, { params: { token } });
      setF({ name: "", slug: "", email: "", password: "" });
      setMsg("Organization created. Send them their email and password.");
      onCreated();
    } catch (err) {
      setMsg(formatApiError(err));
    }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-[#EDE0D4] rounded-2xl p-5 mb-6" data-testid="create-org-form">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-3">add an organization</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input required placeholder="Organization name" value={f.name} onChange={set("name")} className={field} data-testid="create-org-name-input" />
        <input placeholder="Link name (optional, e.g. tpl)" value={f.slug} onChange={set("slug")} className={field} data-testid="create-org-slug-input" />
        <input required type="email" placeholder="Login email" value={f.email} onChange={set("email")} className={field} data-testid="create-org-email-input" />
        <input required minLength={8} placeholder="Password (8+ characters)" value={f.password} onChange={set("password")} className={field} data-testid="create-org-password-input" />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button className="bg-[#E07B39] hover:bg-[#c96a2e] text-white rounded-full px-5 py-2.5 text-[14px] font-medium transition-colors" data-testid="create-org-submit-btn">Create</button>
        {msg && <span className="text-[13px] text-[#8B7355]" data-testid="create-org-message">{msg}</span>}
      </div>
    </form>
  );
}

function OrgRow({ org, token, onChange }) {
  const toggle = async () => {
    await axios.patch(`${API}/admin/orgs/${org.id}`, { active: !org.active }, { params: { token } });
    onChange();
  };
  const reset = async () => {
    const pw = window.prompt(`New password for ${org.email} (8+ characters)`);
    if (!pw || pw.length < 8) return;
    await axios.patch(`${API}/admin/orgs/${org.id}`, { password: pw }, { params: { token } });
    window.alert("Password updated.");
  };
  return (
    <div className="bg-white border border-[#EDE0D4] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`org-row-${org.slug}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#1A1A2E]">{org.name} {!org.active && <span className="font-mono text-[10px] text-[#B4441C] ms-1">PAUSED</span>}</div>
        <div className="font-mono text-[11px] text-[#8B7355] truncate">/o/{org.slug} · {org.email}</div>
      </div>
      <div className="font-mono text-[12px] text-[#1A1A2E]">{org.conversations} chats · {org.questions} questions · {org.documents} docs</div>
      <div className="flex gap-2">
        <button onClick={reset} className="border border-[#EDE0D4] hover:border-[#E07B39] rounded-full px-3 py-1.5 text-[12px]" data-testid={`org-reset-btn-${org.slug}`}>Reset password</button>
        <button onClick={toggle} className="border border-[#EDE0D4] hover:border-[#E07B39] rounded-full px-3 py-1.5 text-[12px]" data-testid={`org-toggle-btn-${org.slug}`}>{org.active ? "Pause" : "Resume"}</button>
      </div>
    </div>
  );
}

export function OrgsPanel({ token }) {
  const [orgs, setOrgs] = useState([]);
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/orgs`, { params: { token } });
    setOrgs(r.data.orgs);
  }, [token]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="orgs-panel">
      <CreateOrgForm token={token} onCreated={load} />
      <div className="space-y-2">
        {orgs.length === 0 && <div className="text-[13px] text-[#8B7355]">No organizations yet.</div>}
        {orgs.map((o) => <OrgRow key={o.id} org={o} token={token} onChange={load} />)}
      </div>
    </div>
  );
}
